import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MdLocationOn, 
  MdPhone, 
  MdClose, 
  MdCheckCircle, 
  MdAdd, 
  MdEdit,
  MdDelete
} from 'react-icons/md';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { toast } from 'react-hot-toast';

import { useAuth } from '../../context/authContext';
import './PurchaseModal.css';

const API = process.env.REACT_APP_API_URL;

const PurchaseModal = ({ 
  user, 
  items = [], 
  subtotal = 0, 
  checkoutType = "cart" ,
  onClose, 
}) => {
  const navigate = useNavigate();
  const { fetchCart, fetchOrders } = useAuth();
  const [dbAddresses, setDbAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const [dbMobiles, setDbMobiles] = useState([]);
  const [selectedMobile, setSelectedMobile] = useState(null);
  const [isAddingMobile, setIsAddingMobile] = useState(false);
  const [editingMobile, setEditingMobile] = useState(null);
  const [mobileInput, setMobileInput] = useState('');

  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('upi');

  const [addressForm, setAddressForm] = useState({
    name: '',
    phone_number: '',           // full E.164 format
    type: 'Home',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    country: 'India'
  });

  const token = localStorage.getItem("token");
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id || storedUser?.id;
  const numericSubtotal = parseFloat(subtotal) || 0;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [addressToDelete, setAddressToDelete] = useState(null);
  // Fetch mobiles
  useEffect(() => {
    const fetchMobiles = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/user/mobiles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch mobiles");
        const data = await res.json();
        setDbMobiles(data);
        if (data.length > 0) {
          setSelectedMobile(data[0]);
        } else {
          setIsAddingMobile(true);
        }
      } catch (err) {
        console.error("Failed to fetch mobiles:", err);
      }
    };
    fetchMobiles();
  }, [token]);

  // Fetch addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/user/addresses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch addresses");
        const data = await res.json();
        setDbAddresses(data);
        if (data.length > 0) {
          setSelectedAddress(data[0]);
        } else {
          setIsAddingAddress(true);
        }
      } catch (err) {
        console.error("Failed to fetch addresses:", err);
      }
    };
    fetchAddresses();
  }, [token]);

  // ── Mobile Handlers (unchanged) ──
  const handleSaveMobile = async () => {
    if (!/^\d{10}$/.test(mobileInput)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    const url = editingMobile 
      ? `${API}/api/user/mobiles/update`
      : `${API}/api/user/mobiles/add`;

    const payload = editingMobile
      ? { id: editingMobile.id, mobile: mobileInput }
      : { mobile: mobileInput };

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const data = await res.json();
        toast.error(data.message || "Failed to save mobile");
        return;
      }

      const refreshRes = await fetch(`${API}/api/user/mobiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = await refreshRes.json();
      setDbMobiles(updated);

      const newMobile = updated.find(m => m.mobile === mobileInput);
      if (newMobile) setSelectedMobile(newMobile);

      setIsAddingMobile(false);
      setEditingMobile(null);
      setMobileInput('');
    } catch (err) {
      toast.error("Failed to save mobile");
    }
  };

  const handleEditMobile = (mob) => {
    setEditingMobile(mob);
    setMobileInput(mob.mobile);
    setIsAddingMobile(true);
  };

  const handleDeleteMobile = async (id) => {
    if (!window.confirm("Delete this mobile number?")) return;

    try {
      const res = await fetch(`${API}/api/user/mobiles/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ id })
      });

      if (!res.ok) throw new Error("Failed to delete mobile");

      const updated = dbMobiles.filter(m => m.id !== id);
      setDbMobiles(updated);
      if (selectedMobile?.id === id) {
        setSelectedMobile(updated[0] || null);
      }
    } catch (err) {
      toast.error("Failed to delete mobile");
    }
  };

  // ── Address Handlers ──
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setAddressForm(prev => ({ ...prev, phone_number: value || '' }));
  };

  const resetAddressForm = () => {
    setAddressForm({
      name: '',
      phone_number: '',
      type: 'Home',
      line1: '',
      line2: '',
      city: '',
      state: '',
      zip: '',
      country: 'India'
    });
    setEditingAddress(null);
    setIsAddingAddress(false);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.name.trim()) return toast.error("Name is required");
    if (!addressForm.phone_number) return toast.error("Phone number is required");
    if (!addressForm.line1.trim() || !addressForm.city.trim() || 
        !addressForm.state.trim() || !addressForm.zip.trim()) {
      return toast.error("Please fill all required fields");
    }
    // inside handleSaveAddress
let mobile = addressForm.phone_number;

// remove leading +91 if present
if (mobile.startsWith('+91')) mobile = mobile.slice(3);

// remove non-digit characters just in case
mobile = mobile.replace(/\D/g, '');

if (!/^\d{10}$/.test(mobile)) {
  toast.error("Invalid 10-digit mobile number");
  return;
}

await fetch(`${API}/api/user/mobiles/add`, {
  method: "POST",
  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  body: JSON.stringify({ mobile })
});
    const url = editingAddress
      ? `${API}/api/user/addresses/update`
      : `${API}/api/user/addresses/add`;

    const payload = editingAddress
      ? { ...addressForm, id: editingAddress.id }
      : addressForm;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to save address");
      }

      const refreshRes = await fetch(`${API}/api/user/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = await refreshRes.json();
      setDbAddresses(updated);

      // Try to select the newly saved/updated one
      const newAddr = updated.find(a => 
        a.line1 === addressForm.line1 && 
        a.city === addressForm.city && 
        a.zip === addressForm.zip
      );
      if (newAddr) setSelectedAddress(newAddr);

      resetAddressForm();
      toast.success(editingAddress ? "Address updated" : "Address added");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to save address");
    }
  };

  const handleEditAddress = (addr) => {
    setEditingAddress(addr);
    setAddressForm({
      name: addr.name || '',
      phone_number: addr.phone_number || '',
      type: addr.type || 'Home',
      line1: addr.line1 || '',
      line2: addr.line2 || '',
      city: addr.city || '',
      state: addr.state || '',
      zip: addr.zip || '',
      country: addr.country || 'India'
    });
    setIsAddingAddress(true);
  };

  const handleDeleteAddress = async (id, confirmed = false) => {
  if (!confirmed) {
    // First call → show confirmation modal
    setAddressToDelete(id);
    setShowDeleteConfirm(true);
    return;
  }

  // Second call (after user confirms) → actually delete
  try {
    const res = await fetch(`${API}/api/user/addresses/delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ id })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to delete address");
    }

    // Update local state
    const updated = dbAddresses.filter(a => a.id !== id);
    setDbAddresses(updated);

    if (selectedAddress?.id === id) {
      setSelectedAddress(updated[0] || null);
    }

    toast.success("Address deleted successfully");
  } catch (err) {
    console.error("Delete failed:", err);
    toast.error(err.message || "Failed to delete address");
  } finally {
    // Always clean up
    setShowDeleteConfirm(false);
    setAddressToDelete(null);
  }
};

  const handleConfirm = async () => {
  if (!selectedAddress) return toast.error('Select a delivery address');
  if (!selectedMobile) return toast.error('Add/select a mobile number');

  setIsProcessing(true);

  try {
    // Prepare address snapshot to send to backend
    const addressSnapshot = {
             // still useful for reference / analytics
      name: selectedAddress.name || '',
      phone_number: selectedAddress.phone_number || '',
      type: selectedAddress.type || 'Home',
      line1: selectedAddress.line1 || '',
      line2: selectedAddress.line2 || null,
      city: selectedAddress.city || '',
      state: selectedAddress.state || '',
      zip: selectedAddress.zip || ''
    
    };

    // Step 1: Create Razorpay order (common for both flows)
    const createOrderRes = await fetch(`${API}/api/payment/create-order`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json", 
        Authorization: `Bearer ${token}` 
      },
      body: JSON.stringify({
        amount: Math.round(numericSubtotal * 100), 
        currency: "INR",
        receipt: `order_rcpt_${Date.now()}`
      })
    });

    if (!createOrderRes.ok) throw new Error("Failed to create order");
    const orderData = await createOrderRes.json();

    // Common payload parts for verification
    const commonVerifyPayload = {
      orderId: orderData.id,
      addressId:selectedAddress.id,
      addressSnapshot,                        // ← NEW: full address copy
      userId,
      items: items.map(i => ({
        product_id: i.id,
        quantity: i.quantity,
        price: i.price,
        product_type: i.product_type
      })),
      paymentMode: selectedPaymentMode,
      checkoutType
    };
    console.log("FINAL ITEMS:", items);
    if (selectedPaymentMode === 'cod') {
      // COD flow – no Razorpay payment
      const verifyRes = await fetch(`${API}/api/payment/verify`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          ...commonVerifyPayload,
          orderId: null,              // no razorpay order for COD
          razorpayPaymentId: null,
          razorpaySignature: null
        })
      });

      const verifyData = await verifyRes.json();
      if (verifyData.success) {
        toast.success("Order placed! Pay on delivery.");
        onClose();
        navigate('/orders');
        fetchOrders()
      } else {
        toast.error(verifyData.message || "Failed to place order");
      }
      setIsProcessing(false);
      return;
    }

    // Online payment → Razorpay flow
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: orderData.amount,
      currency: orderData.currency,
      name: "MediStore",
      description: "Order Payment",
      order_id: orderData.id,
      prefill: {
        name: user.name,
        email: user.email,
        contact: selectedMobile.mobile
      },
      theme: { color: "#F37254" },
      handler: async function (response) {
        try {
          const verifyRes = await fetch(`${API}/api/payment/verify`, {
            method: "POST",
            headers: { 
              "Content-Type": "application/json", 
              Authorization: `Bearer ${token}` 
            },
            body: JSON.stringify({
              ...commonVerifyPayload,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature
            })
          });

          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            toast.success("Payment Successful! Order placed.");
            onClose();
            navigate('/orders');
            fetchOrders()
          } else {
            toast.error(verifyData.message || "Payment verification failed");
          }
        } catch (err) {
          console.error("Verification failed:", err);
          toast.error("Order verification failed");
        } finally {
          setIsProcessing(false);
        }
      },
      modal: {
        ondismiss: () => setIsProcessing(false)
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();

  } catch (err) {
    console.error("Order placement error:", err);
    toast.error("Failed to process order. Please try again.");
    setIsProcessing(false);
  }
};

  return (
    <div className="purchase-modal-overlay">
      <div className="purchase-modal">
        <div className="purchase-modal-header">
          <div className="header-content">
            <h2>Checkout</h2>
            <p>Complete your purchase securely</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        <div className="purchase-modal-body">
          <div className="modal-grid">
            <div className="checkout-details">
              {/* Shipping Address Section */}
              <div className="section address-section">
                <div className="section-title">
                  <MdLocationOn />
                  <h3>Delivery Address</h3>
                  {!isAddingAddress && (
                    <button className="btn-text" onClick={() => setIsAddingAddress(true)}>
                      <MdAdd /> Add New
                    </button>
                  )}
                </div>

                {!isAddingAddress && dbAddresses.length > 0 && (
                  <div className="address-list">
                    {dbAddresses.map((addr) => (
                      <div
                        key={addr.id}
                        className={`address-card ${selectedAddress?.id === addr.id ? 'selected' : ''}`}
                        onClick={() => setSelectedAddress(addr)}
                      >
                        <div className="address-info">
                          <strong>{addr.name || '—'}</strong>
                          <p>{addr.type || '—'}</p>
                          <p>{addr.phone_number ? addr.phone_number : '—'}</p>
                          <p>{addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}</p>
                          <p>{addr.city}, {addr.state} - {addr.zip}</p>
                        </div>
                        <div className="address-actions">
                          <button onClick={(e) => { e.stopPropagation(); handleEditAddress(addr); }}>
                            <MdEdit size={20}/>
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteAddress(addr.id); }}>
                            <MdDelete size={20}/>
                          </button>
                        </div>
                        {selectedAddress?.id === addr.id && <MdCheckCircle className="selected-check" />}
                      </div>
                    ))}
                  </div>
                )}

                {isAddingAddress && (
                  <div className="address-form-inline">
                    <div className="form-row">
                      <input 
                        name="name" 
                        placeholder="Full Name *" 
                        value={addressForm.name} 
                        onChange={handleAddressChange} 
                      />
                    </div>

                    <div className="form-row">
                      <PhoneInput
                        international
                        defaultCountry="IN"
                        placeholder="Phone Number *"
                        value={addressForm.phone_number}
                        onChange={handlePhoneChange}
                        className="phone-input-custom" // add CSS class for styling
                      />
                    </div>

                    <div className="form-row">
                      <select name="type" value={addressForm.type} onChange={handleAddressChange}>
                        <option value="Home">Home</option>
                        <option value="Work">Work</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="form-row">
                      <input 
                        name="line1" 
                        placeholder="Flat, House no., Building *" 
                        value={addressForm.line1} 
                        onChange={handleAddressChange} 
                      />
                    </div>

                    <div className="form-row">
                      <input 
                        name="line2" 
                        placeholder="Area, Street, Sector (optional)" 
                        value={addressForm.line2} 
                        onChange={handleAddressChange} 
                      />
                    </div>

                    <div className="form-row grid-3">
                      <input 
                        name="city" 
                        placeholder="City *" 
                        value={addressForm.city} 
                        onChange={handleAddressChange} 
                      />
                      <input 
                        name="state" 
                        placeholder="State *" 
                        value={addressForm.state} 
                        onChange={handleAddressChange} 
                      />
                      <input 
                        name="zip" 
                        placeholder="PIN Code *" 
                        value={addressForm.zip} 
                        onChange={handleAddressChange} 
                      />
                    </div>

                    <div className="form-actions">
                      <button className="btn-text" onClick={resetAddressForm}>Cancel</button>
                      <button className="btn-primary" onClick={handleSaveAddress}>
                        {editingAddress ? 'Update' : 'Save'} Address
                      </button>
                    </div>
                  </div>
                )}

                {!isAddingAddress && dbAddresses.length === 0 && (
                  <p className="no-address">No addresses found. Please add one.</p>
                )}
              </div>

              {/* Payment Method */}
              <div className="section payment-section">
                <div className="section-title">
                  <MdCheckCircle />
                  <h3>Payment Method</h3>
                </div>
                
                <div className="payment-options">
                  {['upi','card','netbanking','wallet','cod'].map((mode) => (
                    <label key={mode} className={`payment-option ${selectedPaymentMode === mode ? 'selected' : ''}`}>
                      <input 
                        type="radio" 
                        name="paymentMode" 
                        value={mode}
                        checked={selectedPaymentMode === mode}
                        onChange={() => setSelectedPaymentMode(mode)}
                      />
                      {mode.toUpperCase()}
                    </label>
                  ))}
                </div>

                <p style={{ color: 'black', marginTop: '5px' }}>
                  {selectedPaymentMode === 'cod' 
                    ? "Pay when your order is delivered"
                    : "All online payments handled securely via Razorpay"}
                </p>
              </div>
            </div>

            {/* Order Summary (unchanged) */}
            <div className="order-summary">
              <div className="summary-card">
                <h3>Order Summary ({items.length} items)</h3>
                <div className="items-preview">
                  {items.slice(0, 4).map((item, i) => (
                    <div key={i} className="item-row">
                      <span>{item.quantity} × {item.name}</span>
                      <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  {items.length > 4 && <div className="more">+ {items.length - 4} more items</div>}
                </div>
                <div className="divider" />
                <div className="price-row"><span>Subtotal</span><span>₹{numericSubtotal.toFixed(2)}</span></div>
                <div className="divider thick" />
                <div className="total-row">
                  <span>Total</span>
                  <span className="total-amount">₹{numericSubtotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="purchase-modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isProcessing}>
            Cancel
          </button>
          <button 
            className="btn-primary"
            onClick={handleConfirm}
            disabled={isProcessing || !selectedAddress || !selectedMobile}
          >
            {isProcessing 
              ? 'Processing...' 
              : selectedPaymentMode === 'cod'
                ? 'Place Order (COD)' 
                : `Pay ₹${numericSubtotal.toFixed(2)}`}
          </button>
        </div>
      </div>
      {showDeleteConfirm && (
  <div className="delete-confirm-overlay">
    <div className="delete-confirm-modal">
      <div className="delete-header">
        <h3>Delete Address?</h3>
        <button 
          className="close-icon-btn"
          onClick={() => {
            setShowDeleteConfirm(false);
            setAddressToDelete(null);
          }}
        >
          <MdClose size={24} />
        </button>
      </div>

      <div className="delete-body">
        <p>Are you sure you want to delete this address?</p>
      </div>

      <div className="delete-footer">
        <button
          className="btn-cancel"
          onClick={() => {
            setShowDeleteConfirm(false);
            setAddressToDelete(null);
          }}
        >
          Cancel
        </button>
        <button
          className="btn-delete"
          onClick={() => {
            if (addressToDelete) {
              handleDeleteAddress(addressToDelete, true);  // ← triggers actual delete
            }
          }}
        >
          Yes, Delete
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default PurchaseModal;